import type { SVGProps } from "react";

import { IconInfo, Text, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@sentricmusic/ui";

import useGetColours from "../../helpers/getColours";
import { useUIStateStore } from "../../store/uiStateStore";
import { cn } from "../utils/utils";

// Lucide.dev Icons are preferred - go to their icon directory, set the customiser
// parameters to Stroke Width: 1.75 and Size to 24px, then make sure absolute stroke width is set to OFF, then COPY the SVG, take only the path from inside the <svg> tag, and create an IconWrapper component below. - Matt

export function IconWrapper({
  children,
  height,
  className,
  width,
  viewBox = "0 0 24 24",
  disabled = false, // Add a disabled prop
  ...props
}: SVGProps<SVGSVGElement> & { disabled?: boolean }) {
  return (
    <svg
      className={`${className} w-4 h-4 transition ease-out duration-200 ${disabled ? "opacity-15" : ""}`} // Apply Tailwind classes when disabled
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={viewBox}
      fill="none"
      strokeWidth="1.75"
      preserveAspectRatio="xMidYMid meet"
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconInfoWithTooltip({
  tooltip,
  className,
  tooltipClassName,
  size = "h-6 w-6",
  ...props
}: SVGProps<SVGSVGElement> & {
  disabled?: boolean;
  tooltip: string;
  tooltipClassName?: string;
  size?: string;
}) {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { highlightColour } = useGetColours();

  if (!tooltip || tooltip.trim() === "") {
    return <IconInfo className={cn(size, className)} {...props} />;
  }

  const fillOpacity = isTunecore ? "rgba(34, 197, 94, 0.1)" : `${highlightColour}0A`;
  const strokeColor = isTunecore ? "white" : "black";

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div className={tooltipClassName}>
            <IconInfo
              className={cn(
                size,
                "cursor-pointer hover:opacity-50 transition-opacity duration-200",
                className,
              )}
              style={{
                fill: fillOpacity,
                stroke: strokeColor,
              }}
              strokeWidth="1.5"
              {...props}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <Text className="max-w-72">{tooltip}</Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
